/** Pixel scale applied when rasterizing — 2x keeps exported charts crisp. */
const CAPTURE_SCALE = 2;

/**
 * Base64 ceiling the server accepts for a chart snapshot. Mirrors the backend's
 * IMAGE_BASE64_MAX_LENGTH, which sits under the 1 MB request body limit.
 */
const MAX_BASE64_LENGTH = 700_000;

/** Scale steps tried in order when the 2x capture exceeds the wire budget. */
const FALLBACK_SCALES = [1.5, 1, 0.75] as const;

/** Style properties that must be inlined so the detached SVG keeps its theme colors. */
const INLINED_PROPERTIES = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-dasharray',
  'stroke-linecap',
  'stroke-linejoin',
  'opacity',
  'color',
  'font-family',
  'font-size',
  'font-weight',
  'text-anchor',
  'dominant-baseline'
] as const;

/** Copies computed styles onto the clone — CSS variables don't survive serialization. */
function inlineComputedStyles(source: Element, target: Element): void {
  if (target instanceof SVGElement || target instanceof HTMLElement) {
    const computed = window.getComputedStyle(source);
    for (const property of INLINED_PROPERTIES) {
      const value = computed.getPropertyValue(property);
      if (value) target.style.setProperty(property, value);
    }
  }
  const sourceChildren = source.children;
  const targetChildren = target.children;
  for (let i = 0; i < sourceChildren.length; i++) {
    inlineComputedStyles(sourceChildren[i], targetChildren[i]);
  }
}

/**
 * Rasterizes a rendered chart SVG into a base64 PNG (without the data-URL prefix).
 * Resolves theme CSS variables by inlining computed styles, so the export
 * matches what the user sees on screen.
 *
 * A capture larger than the server accepts is redrawn at a smaller scale rather than
 * sent — the request would otherwise be refused after the upload.
 */
export async function captureChartPng(
  svg: SVGSVGElement,
  options?: { background?: string }
): Promise<string> {
  const rect = svg.getBoundingClientRect();
  const width = Math.max(1, Math.round(rect.width));
  const height = Math.max(1, Math.round(rect.height));

  const clone = svg.cloneNode(true) as SVGSVGElement;
  inlineComputedStyles(svg, clone);
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  const markup = new XMLSerializer().serializeToString(clone);
  const svgUrl = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }));

  try {
    const image = await loadImage(svgUrl);
    const background = options?.background ?? '#ffffff';

    let encoded = rasterize(image, width, height, CAPTURE_SCALE, background);
    for (const scale of FALLBACK_SCALES) {
      if (encoded.length <= MAX_BASE64_LENGTH) break;
      encoded = rasterize(image, width, height, scale, background);
    }

    // Still too large at the smallest scale — say so here rather than letting the
    // server reject the upload with an opaque size error.
    if (encoded.length > MAX_BASE64_LENGTH) {
      throw new Error('Chart is too large to export as an image.');
    }
    return encoded;
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}

/** Draws the loaded SVG onto a canvas at one scale and returns the bare base64 PNG. */
function rasterize(
  image: HTMLImageElement,
  width: number,
  height: number,
  scale: number,
  background: string
): string {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas 2D context unavailable.');

  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const dataUrl = canvas.toDataURL('image/png');
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load chart SVG for capture.'));
    image.src = url;
  });
}
